import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  createAdminSponsoredCampaign,
  getAdminSponsoredCampaignById,
  uploadAdminSponsoredCampaignImage,
  updateAdminSponsoredCampaign,
} from "../../utils/adminApi";
import "../Invitations/Invitations.css";
import "./AdminDashboard.css";

const EMPTY_FORM = {
  title: "",
  description: "",
  ctaText: "",
  ctaUrl: "",
  imageUrl: "",
  placements: [],
  priority: 0,
  dismissTtlHours: 3,
  isActive: true,
  startAt: "",
  endAt: "",
};

const PLACEMENT_OPTIONS = [
  "organizer.guests.top",
  "organizer.tables.top",
  "organizer.diary.top",
  "contact.top",
  "profile.bottom",
  "photos.top",
  "upload-photos.bottom",
  "signin.bottom",
  "signup.bottom",
];

const toDateTimeLocal = (value) => {
  if (!value) return "";
  const normalized = String(value).trim();
  if (!normalized) return "";

  const hasTimezoneInfo = /[zZ]|[+-]\d{2}:\d{2}$/.test(normalized);
  if (!hasTimezoneInfo) {
    return normalized.slice(0, 16);
  }

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset();
  const adjusted = new Date(date.getTime() - offset * 60 * 1000);
  return adjusted.toISOString().slice(0, 16);
};

const toBackendDateTime = (value) => {
  if (!value) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return `${normalized}:00`;
};

const normalizePlacements = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

function AdminSponsoredCampaignFormPage() {
  const { authPayload } = useAuth();
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEdit = Boolean(postId);

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const pageTitle = useMemo(
    () => (isEdit ? "Edit Sponsored Campaign" : "New Sponsored Campaign"),
    [isEdit]
  );

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const campaign = await getAdminSponsoredCampaignById(postId);
        setForm({
          title: campaign?.title || "",
          description: campaign?.description || "",
          ctaText: campaign?.ctaText || "",
          ctaUrl: campaign?.ctaUrl || "",
          imageUrl: campaign?.imageUrl || campaign?.image_url || "",
          placements: Array.isArray(campaign?.placements)
            ? campaign.placements
            : typeof campaign?.placements === "string"
              ? campaign.placements
                  .split(",")
                  .map((value) => value.trim())
                  .filter(Boolean)
              : [],
          priority: Number(campaign?.priority) || 0,
          dismissTtlHours: Number(campaign?.dismissTtlHours) || 3,
          isActive: Boolean(campaign?.isActive),
          startAt: toDateTimeLocal(campaign?.startAt),
          endAt: toDateTimeLocal(campaign?.endAt),
        });
      } catch (err) {
        console.error(err);
        setError(err.message || "Неуспешно вчитување на кампањата.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isEdit, postId]);

  if (authPayload?.role !== "admin") {
    return (
      <Box className="invitation-page-container">
        <Alert severity="error">Немате дозвола за пристап до Админ Панел.</Alert>
      </Box>
    );
  }

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (event.target) {
      event.target.value = "";
    }
    setImageFile(file);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    const selectedPlacements = normalizePlacements(form.placements);

    if (!form.title.trim() || !form.ctaUrl.trim()) {
      setError("Title и CTA URL се задолжителни.");
      return;
    }
    if (!selectedPlacements.length) {
      setError("Внесете барем еден placement.");
      return;
    }

    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      ctaText: form.ctaText.trim() || "Дознај повеќе",
      ctaUrl: form.ctaUrl.trim(),
      imageUrl: form.imageUrl.trim() || null,
      placements: selectedPlacements,
      priority: Number(form.priority) || 0,
      dismissTtlHours: Number(form.dismissTtlHours) || 3,
      isActive: Boolean(form.isActive),
      startAt: toBackendDateTime(form.startAt),
      endAt: toBackendDateTime(form.endAt),
    };

    try {
      setSubmitting(true);
      let targetPostId = postId;

      if (isEdit) {
        await updateAdminSponsoredCampaign(postId, payload);
      } else {
        const createdCampaign = await createAdminSponsoredCampaign(payload);
        targetPostId =
          createdCampaign?.id ||
          createdCampaign?.post_id ||
          createdCampaign?.postId ||
          "";
      }

      let uploadMessage = "";
      if (imageFile) {
        if (!targetPostId) {
          throw new Error("Кампањата е зачувана, но недостасува ID за upload на слика.");
        }

        const uploadResponse = await uploadAdminSponsoredCampaignImage(
          targetPostId,
          imageFile
        );
        const nextImageUrl =
          uploadResponse?.imageUrl ||
          uploadResponse?.image_url ||
          uploadResponse?.url ||
          "";

        if (nextImageUrl) {
          setForm((prev) => ({ ...prev, imageUrl: nextImageUrl }));
        }

        setImageFile(null);
        uploadMessage = " Сликата е успешно прикачена.";
      }

      setSuccess(
        `${isEdit ? "Кампањата е ажурирана." : "Кампањата е креирана."}${uploadMessage}`
      );
      setTimeout(() => navigate("/dashboard"), 700);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно зачувување на кампањата.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="invitation-page-container admin-page-container">
      <Stack spacing={2} className="invitation-header" alignItems="center">
        <Typography variant="h4" component="h1">
          {pageTitle}
        </Typography>
      </Stack>

      <Card className="admin-card">
        <CardContent>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

          {loading ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : (
            <Box component="form" onSubmit={onSubmit} className="admin-template-form">
              <Stack spacing={1.5}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  size="small"
                  required
                />
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  multiline
                  minRows={2}
                  size="small"
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="CTA Text"
                    value={form.ctaText}
                    onChange={(e) => handleChange("ctaText", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="CTA URL"
                    value={form.ctaUrl}
                    onChange={(e) => handleChange("ctaUrl", e.target.value)}
                    size="small"
                    fullWidth
                    required
                  />
                </Stack>
                <Stack spacing={1}>
                  <Button component="label" variant="outlined">
                    {imageFile ? "Change Image File" : "Upload Image File"}
                    <input
                      hidden
                      type="file"
                      accept="image/*"
                      onChange={handleImageFileChange}
                    />
                  </Button>
                  {imageFile ? (
                    <Typography variant="body2" color="text.secondary">
                      Selected file: {imageFile.name}
                    </Typography>
                  ) : null}
                </Stack>
                <FormControl size="small" fullWidth>
                  <InputLabel id="placements-label">Placements</InputLabel>
                  <Select
                    labelId="placements-label"
                    multiple
                    value={normalizePlacements(form.placements)}
                    label="Placements"
                    onChange={(e) => {
                      handleChange("placements", normalizePlacements(e.target.value));
                    }}
                    renderValue={(selected) => normalizePlacements(selected).join(", ")}
                  >
                    {PLACEMENT_OPTIONS.map((placement) => (
                      <MenuItem key={placement} value={placement}>
                        {placement}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Priority"
                    type="number"
                    value={form.priority}
                    onChange={(e) => handleChange("priority", e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Dismiss TTL Hours"
                    type="number"
                    value={form.dismissTtlHours}
                    onChange={(e) => handleChange("dismissTtlHours", e.target.value)}
                    size="small"
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Start At"
                    type="datetime-local"
                    value={form.startAt}
                    onChange={(e) => handleChange("startAt", e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="End At"
                    type="datetime-local"
                    value={form.endAt}
                    onChange={(e) => handleChange("endAt", e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.isActive}
                      onChange={(e) => handleChange("isActive", e.target.checked)}
                    />
                  }
                  label="Active"
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {isEdit ? "Update Campaign" : "Create Campaign"}
                  </Button>
                  <Button component={RouterLink} to="/dashboard" variant="text">
                    Back
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default AdminSponsoredCampaignFormPage;
